<script setup>
import { ref, onMounted } from 'vue';
import Swal from 'sweetalert2';
import { ResourceClient } from "@/services/ResourceClient";
const resourceClient = new ResourceClient();

const props = defineProps({
  files: {
    type: Array,
    default: () => []
  },
  lessonId: {
    type: Number,
    default: null
  }
});

const files = ref([]);
const loadedFiles = ref([]);

const emit = defineEmits(['resource-updated']);
const isUploading = ref(false);
const selectedFile = ref(null);
const MAX_FILE_SIZE_MB = 5000; // ✅ Set your max size here (e.g., 500 MB)

// Fetch lesson resources when component mounts or lessonId changes
onMounted(async () => {
  if (props.lessonId) {
    await fetchLessonResources();
  }
});

// Fetch lesson resources from backend
const fetchLessonResources = async () => {
  try {
    const response = await resourceClient.getResourceById(props.lessonId, 'recurso');

    files.value = response.map(resource => ({
      id: resource.id,
      type: resource.tipo,
      name: resource.nombre_archivo,
      url: resource.url,
      isPreview: resource.es_visualizacion
    }));

    // Load all resources
    loadedFiles.value = [];
    for (const resource of response) {
      await loadResource(resource);
    }

  } catch (error) {
    console.error("Error fetching resources:", error);
    Swal.fire('Error', 'No se pudieron cargar los recursos', 'error');
  }
};

// Load resource
const loadResource = async (resource) => {
  try {
    const url = `${process.env.VUE_APP_BACK_END_URL}/resource/download-url?key=${resource.url}`;
    const response = await fetch(url, {
      headers: {
        Accept: 'text/plain',
        Authorization: `Bearer ${sessionStorage.getItem('token') || ''}`
      }
    });

    const responseText = await response.text();

    if (!response.ok) {
      throw new Error(`Request failed with status ${response.status}: ${response.statusText}`);
    }

    loadedFiles.value.push({
      id: resource.id,
      type: getFileType(resource.tipo || resource.url.split('.').pop()),
      name: resource.nombre_archivo,
      url: resource.url,
      downloadUrl: responseText,
      isPreview: resource.es_visualizacion,
      size: resource.size || 'N/A'
    });

  } catch (error) {
    console.error('❌ Error loading resource:', error);
    loadedFiles.value.push({
      id: resource.id,
      type: getFileType(resource.tipo || resource.url.split('.').pop()),
      name: resource.nombre_archivo,
      url: resource.url,
      downloadUrl: null,
      isPreview: resource.es_visualizacion,
      size: resource.size || 'N/A'
    });
  }
};

const handleFileChange = (event) => {
  const file = event.target.files[0];
  if (file) {
    const sizeInMB = file.size / (1024 * 1024);
    if (sizeInMB > MAX_FILE_SIZE_MB) {
      Swal.fire(
        'Archivo demasiado grande',
        `El archivo excede el límite de ${MAX_FILE_SIZE_MB} MB.`,
        'warning'
      );
      selectedFile.value = null;
      event.target.value = ''; // Clear input
    } else {
      selectedFile.value = file;
    }
  }
};

const uploadResource = async () => {
  if (!selectedFile.value) {
    Swal.fire('Error', 'Please select a file first', 'error');
    return;
  }

  try {
    isUploading.value = true;
    const mimeType = selectedFile.value.type;
    const fileName = selectedFile.value.name;
    const fileSize = (selectedFile.value.size / (1024 * 1024)).toFixed(2) + ' MB';

    const uploadUrlResponse = await resourceClient.getUploadUrl(mimeType);
    const { url: presignedUrl, key } = uploadUrlResponse;

    const uploadResponse = await fetch(presignedUrl, {
      method: 'PUT',
      body: selectedFile.value,
      headers: {
        'Content-Type': mimeType
      }
    });

    if (!uploadResponse.ok) {
      throw new Error('S3 upload failed');
    }

    await resourceClient.createResource({
      leccion_id: props.lessonId,
      tipo: getFileType(mimeType),
      url: key,
      nombre_archivo: fileName,
      es_visualizacion: false,
      size: fileSize
    });

    emit('resource-updated');
    await fetchLessonResources();
    Swal.fire('Éxito', '¡Recurso subido exitosamente!', 'success');
    selectedFile.value = null;
  } catch (error) {
    console.error('Upload failed:', error);
    Swal.fire('Error', 'Failed to upload resource', 'error');
  } finally {
    isUploading.value = false;
  }
};

const removeResource = async (resourceId) => {
  try {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: "Esta acción no se puede deshacer",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar'
    });

    if (result.isConfirmed) {
      await resourceClient.deleteResource(resourceId)
      emit('resource-updated');
      await fetchLessonResources();
      Swal.fire('Eliminado', 'El recurso ha sido removido', 'success');
    }
  } catch (error) {
    console.error('Error al eliminar recurso:', error);
    Swal.fire('Error', 'No se pudo eliminar el recurso', 'error');
  }
};

const getFileType = (mimeTypeOrExt) => {
  const typeMap = {
    // Documents
    'pdf': 'pdf',
    'application/pdf': 'pdf',
    'doc': 'word',
    'docx': 'word',
    'application/msword': 'word',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'word',
    'xls': 'excel',
    'xlsx': 'excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
    'ppt': 'powerpoint',
    'pptx': 'powerpoint',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'powerpoint',
    'txt': 'text',
    'text/plain': 'text',

    // Images
    'jpg': 'image',
    'jpeg': 'image',
    'png': 'image',
    'gif': 'image',
    'svg': 'image',
    'image/jpeg': 'image',
    'image/png': 'image',
    'image/gif': 'image',
    'image/svg+xml': 'image',

    // Videos
    'mp4': 'video',
    'webm': 'video',
    'avi': 'video',
    'mov': 'video',
    'video/mp4': 'video',
    'video/webm': 'video',

    // Audio
    'mp3': 'audio',
    'wav': 'audio',
    'audio/mpeg': 'audio',
    'audio/wav': 'audio',

    // Archives
    'zip': 'archive',
    'rar': 'archive',
    '7z': 'archive',
    'application/zip': 'archive',
    'application/vnd.rar': 'archive',
    'application/x-7z-compressed': 'archive',

    // Executables
    'exe': 'exe',
    'application/x-msdownload': 'exe',

    // Default
    'default': 'file'
  };

  // Check if input is extension or mime type
  const key = mimeTypeOrExt.includes('/') ?
    mimeTypeOrExt.toLowerCase() :
    mimeTypeOrExt.toLowerCase().replace('.', '');

  return typeMap[key] || typeMap['default'];
};

const getFileIcon = (fileType) => {
  const icons = {
    'pdf': 'fas fa-file-pdf text-danger',
    'word': 'fas fa-file-word text-primary',
    'excel': 'fas fa-file-excel text-success',
    'powerpoint': 'fas fa-file-powerpoint text-warning',
    'text': 'fas fa-file-alt text-info',
    'image': 'fas fa-file-image text-primary',
    'video': 'fas fa-file-video text-info',
    'audio': 'fas fa-file-audio text-secondary',
    'archive': 'fas fa-file-archive text-warning',
    'exe': 'fas fa-file-code text-danger',
    'file': 'fas fa-file text-secondary'
  };
  return icons[fileType] || icons['file'];
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('es-ES', options);
};
</script>

<template>
  <div class="row mt-4">
    <div class="col-12">
      <div class="card-body pt-0">
        <h6 class="font-weight-bolder"><i class="fas fa-paperclip pe-2"></i> Recursos del Curso</h6>
        <div class="col-12">

          <!-- File upload section -->
          <div class="file-upload-container mb-4 mt-4">
            <input type="file" id="resource-upload" accept="*" @change="handleFileChange" class="d-none">

            <div class="d-flex flex-column gap-2"> <!-- WRAP BOTH -->
              <label for="resource-upload" class="btn btn-primary w-100 mb-0">
                <i class="fas fa-upload me-2"></i>
                {{ selectedFile ? selectedFile.name : 'Cargar archivo (máx. 5000 MB)' }}
              </label>


              <button v-if="selectedFile" @click="uploadResource" class="btn btn-info w-100" :disabled="isUploading">
                <span v-if="isUploading">
                  <i class="fas fa-spinner fa-spin me-2"></i>Cargando...
                </span>
                <span v-else>
                  <i class="fas fa-cloud-upload-alt me-2"></i>Subir Recurso
                </span>
              </button>
            </div>
          </div>


          <!-- Resources list -->
          <div v-if="loadedFiles.length" class="mt-4">
            <div class="table-responsive">
              <table class="table table-hover">
                <tbody>
                  <tr v-for="file in loadedFiles" :key="file.id">
                    <td class="fa-lg ">
                      <i :class="getFileIcon(file.type)"
                        class="fa-lg d-flex justify-content-center align-items-center"></i>
                    </td>
                    <td class="text-nowrap">
                      {{ file.name }}
                    </td>
                    <td>
                      <div class="d-flex gap-2 justify-content-end align-items-center">
                        <a v-if="file.downloadUrl" :href="file.downloadUrl" target="_blank"
                          class="btn btn-sm mb-0 px-3 btn-dark" :title="`Descargar ${file.name}`">
                          <i class="fas fa-download"></i>
                        </a>
                        <button @click="removeResource(file.id)" class="btn btn-sm mb-0 btn-danger"
                          title="Eliminar recurso">
                          <i class="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div v-else class="text-center py-4">
            <p class="text-muted">No hay recursos disponibles para esta lección.</p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.file-upload-container {
  justify-content: flex-start;
  align-items: center;
  margin-bottom: 1rem;
}

.table-responsive {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.table {
  margin-bottom: 0;
}

.table th {
  background-color: #f8f9fa;
  font-weight: 600;
}

.table-hover tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.badge {
  font-size: 0.75em;
  text-transform: uppercase;
}

.btn-sm {
  padding: 0.25rem 0.5rem;
  font-size: 0.8rem;
}

.text-nowrap {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 250px;
}
</style>